import { ChevronRight, Mail } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { DynamicForm } from '@shadcn/components/dynamic-form';
import { useGetAuthConfig, useUpdateAuthConfig } from '@shadcn/hooks/use-auth';
import {
  authEmailProviderSchema,
  authFieldLabels,
  authGeneralSettingsSchema,
  type AuthGeneralSettingsSchema,
} from '@shadcn/lib/schemas/auth';
import { z } from 'zod';
import { Alert } from '~/components/ui/Alert';
import { Button } from '~/components/ui/Button';
import { Skeleton } from '~/components/ui/Skeleton';
import { useSheetNavigation } from '~/lib/context/sheetNavigation';

function ProviderSettingsView({
  schema,
  title,
  initialValues: allInitialValues,
  onSuccess,
}: {
  schema: z.ZodObject<any> | z.ZodEffects<z.ZodObject<any>>;
  title: string;
  initialValues: any;
  onSuccess: () => void;
}) {
  const { mutate: updateAuthConfig, isPending: isUpdatingConfig } = useUpdateAuthConfig();

  const actualSchema = 'shape' in schema ? schema : (schema._def.schema as z.ZodObject<any>);

  const handleUpdateAuthConfig = (formData: z.infer<typeof actualSchema>) => {
    const payload = Object.fromEntries(Object.entries(formData).filter(([_, value]) => value !== undefined));

    if (Object.keys(payload).length === 0) {
      alert('No changes to submit. Please modify a field to update.');
      return;
    }

    updateAuthConfig(
      {
        payload,
      },
      {
        onSuccess,
      },
    );
  };

  const formInitialValues = useMemo(() => {
    if (!allInitialValues) {
      return undefined;
    }

    const schemaKeys = Object.keys(actualSchema.shape);

    const result = schemaKeys.reduce(
      (acc, key) => {
        if (Object.prototype.hasOwnProperty.call(allInitialValues, key)) {
          acc[key] = allInitialValues[key];
        }

        return acc;
      },
      {} as Record<string, any>,
    );

    return result;
  }, [allInitialValues, actualSchema]);

  return (
    <div className="p-6 pt-4 lg:p-8 lg:pt-8">
      <h2 className="lg:text-xl font-semibold mb-2 lg:mb-4">{title}</h2>
      <DynamicForm
        schema={actualSchema}
        onSubmit={handleUpdateAuthConfig}
        isLoading={isUpdatingConfig}
        invertedFields={['mailer_autoconfirm']} // if mailer_autoconfirm is false, confirm email switch should be enabled
        initialValues={formInitialValues}
        labels={authFieldLabels}
      />
    </div>
  );
}

export function AuthManager() {
  const { data: authConfigData, isLoading: isLoadingConfig, error: errorLoadingConfig } = useGetAuthConfig();

  const { mutate: updateAuthConfig, isPending: isUpdatingConfig } = useUpdateAuthConfig();
  const { push, pop } = useSheetNavigation();

  const handleUpdateGeneralSettings = (formData: AuthGeneralSettingsSchema) => {
    const payload = Object.fromEntries(Object.entries(formData).filter(([_, value]) => value !== undefined));

    if (Object.keys(payload).length === 0) {
      alert('No changes to submit. Please modify a field to update.');
      return;
    }

    updateAuthConfig({
      payload,
    });
  };

  const providers: {
    name: string;
    icon: React.ReactNode;
    description: string;
    schema: z.ZodObject<any> | z.ZodEffects<z.ZodObject<any>>;
  }[] = [
    {
      icon: <Mail className="h-4 w-4 text-bolt-ds-textSecondary" />,
      name: 'Email',
      description: 'Sign in with email and password',
      schema: authEmailProviderSchema,
    },
  ];

  const handleProviderClick = useCallback(
    (provider: { name: string; schema: z.ZodObject<any> | z.ZodEffects<z.ZodObject<any>> }) => {
      push({
        title: `${provider.name} Provider Settings`,
        component: (
          <ProviderSettingsView
            schema={provider.schema}
            title={`${provider.name} Provider Settings`}
            initialValues={authConfigData}
            onSuccess={() => pop()}
          />
        ),
      });
    },
    [authConfigData, push, pop],
  );

  const formInitialValues = useMemo(() => {
    if (!authConfigData) {
      return undefined;
    }

    const schemaKeys = Object.keys(authGeneralSettingsSchema.shape);

    return schemaKeys.reduce(
      (acc, key) => {
        if (Object.prototype.hasOwnProperty.call(authConfigData, key)) {
          acc[key] = authConfigData[key as keyof typeof authConfigData];
        }

        return acc;
      },
      {} as Record<string, any>,
    );
  }, [authConfigData]);

  if (errorLoadingConfig) {
    return (
      <div className="p-6 pt-4 lg:p-8 lg:pt-8">
        <Alert type="danger" title="Error loading auth settings">
          There was a problem loading your authentication configuration.
        </Alert>
      </div>
    );
  }

  if (isLoadingConfig) {
    return (
      <div className="p-6 pt-4 lg:p-8 lg:pt-8 space-y-2">
        <Skeleton className="h-12 w-full mb-8" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  return (
    <div className="p-6 pt-4 lg:p-8 lg:pt-8">
      <div className="mb-12">
        <h2 className="text-base lg:text-xl font-semibold">General Settings</h2>
        <p className="text-sm lg:text-base text-bolt-ds-textSecondary mt-1 mb-2">Allow people to sign up to your app</p>
        <DynamicForm
          schema={authGeneralSettingsSchema}
          onSubmit={handleUpdateGeneralSettings}
          isLoading={isUpdatingConfig}
          initialValues={formInitialValues}
          labels={authFieldLabels}
        />
      </div>
      <div>
        <h2 className="font-semibold lg:text-lg">Sign in methods</h2>
        <p className="text-bolt-ds-textSecondary mb-6 mt-1 text-sm lg:text-base">
          Configure how people sign in and up to your app.
        </p>
        <div className="border border-bolt-elements-borderColor rounded-md overflow-hidden bg-bolt-ds-surfaceOne">
          {providers.map((provider) => (
            <Button
              variant="ghost"
              size="lg"
              onClick={() => handleProviderClick(provider)}
              key={provider.name}
              truncate={false}
              className="rounded-none justify-start flex-row text-left h-auto px-8 py-4 w-full gap-4 border-b border-bolt-elements-borderColor last:border-b-0"
            >
              {provider.icon}
              <div className="flex-1">
                <h3 className="font-bold">{provider.name}</h3>
                <p className="text-bolt-ds-textSecondary text-sm">{provider.description}</p>
              </div>
              <ChevronRight className="h-4 w-4 text-bolt-ds-textSecondary" />
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}
